import pdfMake from 'pdfmake/build/pdfmake';
import Logo from 'Icons/RMlogo.png';
import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import { getParentPath, convertRGBToHex, LayerType } from 'utils/Global';
import { TreeService } from 'business/services/TreeService';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import PlateService from 'business/services/PlateService';
import { IngredientReportOptionsDTO, fonts } from './ReportsConstants';
import pdfFonts from './vfs_fonts';
import { WellNavigationDetails } from 'components/Experiment/constants';
import { createPdfWithPopupBlockerHandler } from './utils';
import {
  FragmentLayerDTO,
  ProteinLayerDTO,
  ScreenLayerDTO,
  SeedLayerDetailsDTO,
} from 'business/models/Models';
import { ELayersDetails } from 'enums/ELayersView';

export const TABLE_LAYOUT = {
  hLineWidth: function (i, node) {
    return 0.5;
  },
  vLineWidth: function (i, node) {
    return 0.5;
  },
};

export async function generateIngredientReportPDF(
  options: IngredientReportOptionsDTO,
  expData: ExperimentDtoClient,
  wellDetails: WellNavigationDetails[],
  dropNumber: number,
  saveReport: boolean,
  onPopupBlocked?: () => void,
) {
  pdfMake.vfs = pdfFonts;
  pdfMake.fonts = fonts;

  const pdfDefinition = {
    info: {
      title: expData.expName + ' - Ingredient Report',
      author: 'Rock Maker',
      subject: 'Experiment',
    },
    content: await CreateContent(options, expData, wellDetails, dropNumber),
    styles: {
      headerText: { fontSize: 10, bold: true, lineHeight: 1.5 },
      largeText: { fontSize: 10, lineHeight: 1.5 },
      labelText: { fontSize: 8, lineHeight: 1.5 },
      tableSubHeader: { bold: true, fillColor: '#cccccc' },
      tableAltRow: { fillColor: '#f5f5f5' },
      textBold: { bold: true },
    },
    defaultStyle: {
      fontSize: 7,
      lineHeight: 1.2,
      font: options.fontFamily.key,
      bold:
        options.fontStyle == 'Bold' || options.fontStyle == 'Bold Italic' ? true : false,
      italics:
        options.fontStyle == 'Italic' || options.fontStyle == 'Bold Italic'
          ? true
          : false,
    },
    pageMargins: [10, 50, 10, 0],
    pageOrientation: options.rowLimit > options.columnLimit ? 'portrait' : 'landscape',
    pageSize: 'A4',
    header: getHeader(),
    pageBreakBefore: false,
  };

  createPdfWithPopupBlockerHandler(
    pdfMake,
    pdfDefinition,
    expData.expName + ' - Ingredients Report',
    saveReport,
    onPopupBlocked,
  );
}

export async function CreateContent(
  options: IngredientReportOptionsDTO,
  expData: ExperimentDtoClient,
  wellDetails: WellNavigationDetails[],
  dropNumber: number,
) {
  const content = [];
  if (options.fitToPage === true) {
    options.columnLimit = expData.containerCols;
    options.rowLimit = expData.containerRows;
  }
  const wellInfo = wellDetails[0];
  const plateInfo: any = expData.plateDto.find(
    (plate) => plate.plateId === wellInfo.plateID,
  );

  const treeService = new TreeService();
  const expParents = await treeService.getDocumentParentNodes(
    expData.experimentId,
    ETreeNodeTypes.Experiment,
    true,
  );
  const expPath = expParents ? getParentPath(expParents) : '';
  let _dropNumber = 0;
  let pageBreak;

  if (options.isScreenExperiment) {
    _dropNumber = null;
    const data = await getWellsTable(
      options,
      _dropNumber,
      expData,
      plateInfo.plateId,
      wellInfo,
    );

    if (data.length > 0) {
      content.push({
        text:
          'Experiment: ' +
          expPath +
          '   Plate ID:' +
          plateInfo.plateId +
          '   Barcode:' +
          plateInfo.plateBarName.split(',')[2],
        alignment: 'center',
        pageBreak: pageBreak,
      });
      content.push(await designTableData(options, data, expData));
      pageBreak = 'before';
    } else {
      content.push({
        text: 'The document does not contain any pages.',
        alignment: 'center',
        fontSize: 12,
        color: 'grey',
      });
    }
  } else {
    for (const drop of options.drops) {
      if (drop.checked) {
        _dropNumber = dropNumber;
        const data = await getWellsTable(
          options,
          drop.dropNumber,
          expData,
          plateInfo.plateId,
          wellInfo,
        );

        if (data.length > 0) {
          content.push({
            text:
              'Experiment: ' +
              expPath +
              '   Plate ID:' +
              plateInfo.plateId +
              '   Barcode:' +
              plateInfo.plateBarName.split(',')[2] +
              '   Drop Number:' +
              drop.dropNumber,
            alignment: 'center',
            pageBreak: pageBreak,
          });
          content.push(await designTableData(options, data, expData));
          pageBreak = 'before';
        } else {
          content.push({
            text: 'The document does not contain any pages.',
            alignment: 'center',
            fontSize: 12,
            color: 'grey',
          });
        }
      }
    }
  }

  content.push('\n');
  content.push('\n');
  return content;
}

export async function designTableData(
  options: IngredientReportOptionsDTO,
  data: any,
  expData: ExperimentDtoClient,
) {
  const columnLimit = options.columnLimit;
  const rowLimit = options.rowLimit;
  const rows: any[] = [];
  let columns: any[] = [];
  let rowCount = 1;
  let rowStart = 0;
  let columnStart = 1;
  let rowIndex = 0;
  while (rowIndex < expData.containerRows) {
    for (let i = rowStart; i < rowLimit + rowStart; i++) {
      let columnCount = 1;

      for (let j = columnStart; j < columnLimit + columnStart; j++) {
        if (columnCount === 1) {
          if (i < data.length) {
            columns.push({
              index: '',
              data: data[i].columns[0],
            });
          }
        }
        if (i < data.length) {
          columns.push({
            index: j,
            data: data[i].columns[j],
          });
        }
        if (columnCount === columnLimit || j === expData.containerCols) {
          rows.push(columns);
          columns = [];
          columnCount = 0;
        }
        columnCount++;
        if (j === expData.containerCols) {
          break;
        }
      }

      if (rowCount === rowLimit) {
        columnStart = columnStart + columnLimit;
        if (i >= expData.containerRows && columnStart > expData.containerCols) {
          rowIndex = rowStart + rowLimit;
          break;
        }
        if (columnStart > expData.containerCols) {
          columnStart = 1;
          rowStart = rowStart + rowLimit;
          rowIndex = rowStart;
          rowCount = 0;
        } else {
          rowStart = rowIndex;
          rowCount = 0;
        }
      }
      rowCount++;
      if (i === expData.containerRows && columnStart > expData.containerCols) {
        rowIndex = rowStart + rowLimit;
        break;
      }
    }
  }

  let loopCount = 0;
  while (loopCount < rows.length) {
    if (rows[loopCount].length === 0) {
      rows.splice(loopCount, 1);
    } else {
      ++loopCount;
    }
  }

  let lastRows: any[] = [];
  const rowTable: any[] = [];

  let rowNumber = 0;

  for (const row of rows) {
    rowNumber++;
    if (row.length > 0) {
      if (lastRows.length === 0) {
        let columnTop: any[] = [];
        row.forEach((col) => {
          columnTop.push({
            data: [
              {
                text: col.index,
                alignment: 'center',
                border: [false, false, false, false],
              },
            ],
          });
        });

        lastRows.push(columnTop);
        columnTop = [];
      }

      lastRows.push(row);
      if (lastRows.length === options.rowLimit + 1) {
        let pageBreak = 'after';
        if (rowNumber == rows.length) {
          pageBreak = '';
        }

        rowTable.push(await getFinalTable(lastRows, pageBreak, options));
        lastRows = [];
      }
    }
  }
  return rowTable;
}

async function getFinalTable(
  rows: any[],
  pageBreak: string,
  options: IngredientReportOptionsDTO,
) {
  const rows1: any[] = [];
  let tableColumns: any[] = [];

  rows.forEach((row) => {
    if (row.length > 0) {
      row.forEach((col) => {
        if (col.index === '') {
          const tempData = { ...col.data[0] };
          tableColumns.push(tempData);
        } else {
          tableColumns.push(col.data[0]);
        }
      });

      rows1.push(tableColumns);
      tableColumns = [];
    }
  });

  const numberOfColumns = rows1[0].length - 1;
  const widths = [];

  widths.push('1%');
  const singleColumnWidth = 99 / options.columnLimit;
  for (let i = 0; i < numberOfColumns; i++) {
    widths.push(singleColumnWidth.toString() + '%');
  }

  return {
    pageBreak: pageBreak,
    table: {
      widths: widths,
      headerRows: rows1.length,
      body: rows1,
    },
    layout: TABLE_LAYOUT,
  };
}

const isLayersAndValueValid = (
  expData: ExperimentDtoClient,
  expectedLayer: ELayersDetails,
  dropVolume: number,
) => {
  const isFoundMatchedLayer = expData.layers.find((layer) => {
    switch (expectedLayer) {
      case ELayersDetails.PROTEIN:
        return layer.layerDetail instanceof ProteinLayerDTO;
      case ELayersDetails.FRAGMENT:
        return layer.layerDetail instanceof FragmentLayerDTO;
      case ELayersDetails.SEED:
        return layer.layerDetail instanceof SeedLayerDetailsDTO;
      case ELayersDetails.SCREEN:
        return layer.layerDetail instanceof ScreenLayerDTO;
    }
  });

  if (isFoundMatchedLayer && dropVolume !== undefined && dropVolume !== null) {
    return true;
  }
  return false;
};

export async function getWellsTable(
  options: IngredientReportOptionsDTO,
  dropNumber: number,
  expData: ExperimentDtoClient,
  plateId: number,
  wellInfo: any,
) {
  const rows: any = [];
  let columns: any = [];
  const wellData = await getWellData(expData, wellInfo, dropNumber);
  const plateService = new PlateService();
  const plateScores = await plateService.getPlateScoreBasedOnWellsByPlateId(plateId);

  let width;
  let rowHeight;
  let tableHeight;
  if (options.rowLimit > options.columnLimit) {
    tableHeight = 650;
    width = 500 / options.columnLimit;
    rowHeight = 800 / options.rowLimit;

    if (rowHeight < width) {
      width = rowHeight;
    }
  } else {
    width = 760 / options.columnLimit;
    rowHeight = 500 / options.rowLimit;

    if (rowHeight < width) {
      width = rowHeight;
    }

    tableHeight = 420;
  }

  let dropImageCount = 0;
  const columnLoopCount = expData.containerCols + 1;
  for (let i = 0; i < expData.containerRows; i++) {
    for (let j = 0; j < columnLoopCount; j++) {
      if (j === 0) {
        columns.push([
          {
            width: 20,
            text: String.fromCharCode('A'.charCodeAt(0) + i),
            alignment: 'center',
            margin: [0, tableHeight / options.rowLimit / 2 - 10, 0, 0],
            border: [false, false, false, false],
          },
        ]);
      } else {
        const wellScores: any[] = [];
        if (plateScores.length > 0) {
          const inspectionSet: any = expData.inspections.find(
            (insp) => insp.id == expData.expSelectedStates.inspectionSetId,
          );
          const imageBatchId: number = inspectionSet.groupedInspections.find(
            (gi) => gi.plateId == plateId,
          ).imageBatchId;
          plateScores.forEach((scores) => {
            scores.forEach((wellScore) => {
              if (
                wellScore.wellNumber == wellData[dropImageCount].wellNumber &&
                wellScore.dropNumber == dropNumber &&
                wellScore.scoreBatchId == imageBatchId
              ) {
                wellScores.push(wellScore);
              }
            });
          });
        }
        const scoresBody: any[] = [];
        const scoreTextArray: any[] = [];
        scoresBody.push({
          text: '',
          border: [false, false, false, false],
        });

        scoreTextArray.push(' ');
        wellScores.forEach((wellScore) => {
          let scoreText = '';

          if (wellScore.isAutoScore) {
            if (wellScore.autoScoreValue == 1) {
              scoreText = '+';
            } else if (wellScore.autoScoreValue == 0) {
              scoreText = '-';
            } else if (wellScore.autoScoreValue == null) {
              scoreText = ' ';
            } else {
              scoreText = wellScore.autoScoreValue.toFixed(2);
            }
          } else {
            scoreText = wellScore.hotKey;
          }
          scoresBody.push({
            text: scoreText,
            alignment: 'center',
            fillColor: convertRGBToHex(wellScore.colorRGB),
            color: 'white',
            border: [false, false, true, false],
          });
          scoreTextArray.push(scoreText);
        });

        let singleDigitScoreWidth = 2;
        for (let i = 1; i < options.columnLimit; i++) {
          singleDigitScoreWidth = singleDigitScoreWidth + 0.75;
        }

        const scoresWidth: any[] = [];
        for (let i = 0; i < scoresBody.length; i++) {
          if (i == 0) {
            scoresWidth.push('*');
          } else {
            if (scoreTextArray[i].length == 1) {
              scoresWidth.push(singleDigitScoreWidth.toString() + '%');
            } else {
              scoresWidth.push('auto');
            }
          }
        }

        let columnCount = 0;
        const ingredientDataOptions: any = [];
        if (options.concentration) {
          columnCount = columnCount + 1;
          ingredientDataOptions.push({
            text: options.columnLimit > 6 ? 'C' : 'Conc',
            bold: true,
            fillColor: '#808080',
            color: '#FFFFFF',
            alignment: 'center',
          });
        }
        if (options.pHVolumes) {
          columnCount = columnCount + 2;
          ingredientDataOptions.push({
            text: options.columnLimit > 6 ? 'L' : 'L V',
            bold: true,
            fillColor: '#808080',
            color: '#FFFFFF',
            alignment: 'center',
          });

          ingredientDataOptions.push({
            text: options.columnLimit > 6 ? 'H' : 'H V',
            bold: true,
            fillColor: '#808080',
            color: '#FFFFFF',
            alignment: 'center',
          });
        }
        if (options.pH) {
          columnCount = columnCount + 1;
          ingredientDataOptions.push({
            text: 'pH',
            bold: true,
            fillColor: '#808080',
            color: '#FFFFFF',
            alignment: 'center',
            margin: [0, 0, 0, 0],
            padding: [0, 0, 0, 0],
          });
        }

        let rowCount = 0;
        const headerRowCount = 1;
        const wellVolumeRowCount = 1;

        rowCount = rowCount + headerRowCount + wellVolumeRowCount;
        if (options.showProteinName) {
          rowCount = rowCount + 1;
        }
        if (options.showDropVolumes) {
          rowCount = rowCount + 1;
        }
        if (options.showWellVolumes) {
          rowCount = rowCount + 1;
        }
        if (wellData[dropImageCount].wellIngredientList.length > 0) {
          if (options.showColumnNameHeading) {
            rowCount = rowCount + 1;
          }

          for (let i = 0; i < options.maxIngredients; i++) {
            rowCount = rowCount + 2;
          }
        } else {
          rowCount = rowCount + 1;

          for (let i = 0; i < options.maxIngredients; i++) {
            for (let j = 0; j < 2; j++) {
              rowCount = rowCount + 1;
            }
          }
        }

        //font size calculation
        const cellRowHeight = tableHeight / options.rowLimit / rowCount;

        const wellWidth =
          options.rowLimit > options.columnLimit
            ? 500 / options.columnLimit
            : 760 / options.columnLimit;
        const avgColumnWidth = wellWidth / columnCount;

        const fontSizeByHeight = cellRowHeight * 0.5;

        const fontSizeByWidth = avgColumnWidth / 4 / 0.6;

        let fontSize = Math.min(fontSizeByHeight, fontSizeByWidth);

        const MIN_FONT_SIZE = 2;
        const MAX_FONT_SIZE = 10;

        fontSize = Math.max(MIN_FONT_SIZE, Math.min(fontSize, MAX_FONT_SIZE));

        const totalWells = options.columnLimit * options.rowLimit;
        if (totalWells >= 96) {
          let densityScale = 1.0;
          if (totalWells >= 384) {
            densityScale = Math.sqrt(96 / totalWells);
          } else if (totalWells > 96) {
            densityScale = 96 / totalWells;
          }
          fontSize = fontSize * densityScale;
        }

        fontSize = Math.max(0.5, fontSize);

        const singleColumnMaxWidth =
          options.columnLimit > options.rowLimit
            ? 750 / options.columnLimit
            : 550 / options.columnLimit;

        const maxChars = Math.floor(singleColumnMaxWidth / (fontSize * 0.6));

        const body: any[] = [];

        const headerArray: any[] = [];
        headerArray.push({
          columns: [
            {
              width: '70%',
              text:
                'Plate ID ' +
                wellData[dropImageCount].plateId +
                ', ' +
                wellData[dropImageCount].wellName +
                (wellData[dropImageCount].dropNumber
                  ? '-' + wellData[dropImageCount].dropNumber
                  : ''),
              bold: true,
            },
            {
              width: '*',
              margin: [getScoreTableLeftMagin(options), 0, -3, 0],
              table: {
                widths: scoresWidth,
                body: [scoresBody],
              },
              layout: {
                hLineWidth: function hLineWidth(i) {
                  return 0.1;
                },
                vLineWidth: function vLineWidth(i) {
                  return 0.1;
                },
                paddingLeft: function (i, node) {
                  return 0;
                },
                paddingRight: function (i, node) {
                  return 0;
                },
                paddingTop: function (i, node) {
                  return 0;
                },
                paddingBottom: function (i, node) {
                  return 0;
                },
              },
            },
          ],
          colSpan: columnCount,
          border: [true, false, true, false],
        });

        for (let i = 1; i < columnCount; i++) {
          headerArray.push({});
        }
        body.push(headerArray);

        {
          const wellVolumeArray: any[] = [];
          wellVolumeArray.push({
            text: wellData[dropImageCount].wellScreenLayerName
              ? wellData[dropImageCount].wellScreenLayerName
              : ' ',
            colSpan: columnCount,
            border: [true, false, true, false],
          });

          for (let i = 1; i < columnCount; i++) {
            wellVolumeArray.push({});
          }

          body.push(wellVolumeArray);
        }

        if (options.showProteinName) {
          const proteinNameArray: any[] = [];
          const proteinName = wellData[dropImageCount].proteinFormulationName || '';

          const prefix = 'Protein: ';
          const maxProteinNameLength = Math.max(1, maxChars - prefix.length / 2);
          const truncatedProteinName =
            proteinName.length > maxProteinNameLength
              ? proteinName.slice(0, maxProteinNameLength) + '...'
              : proteinName;

          proteinNameArray.push({
            text: proteinName ? prefix + truncatedProteinName : '',
            colSpan: columnCount,
            border: [true, false, true, false],
          });

          for (let i = 1; i < columnCount; i++) {
            proteinNameArray.push({});
          }

          body.push(proteinNameArray);
        }

        if (options.showDropVolumes) {
          const drop = wellData[dropImageCount];
          const parts = [
            isLayersAndValueValid(
              expData,
              ELayersDetails.PROTEIN,
              drop.dropProteinVolume,
            ) && `${drop.dropProteinVolume} μL p`,
            isLayersAndValueValid(expData, ELayersDetails.PROTEIN, drop.dropWellVolume) &&
              `${drop.dropWellVolume} μL w`,
            isLayersAndValueValid(expData, ELayersDetails.SEED, drop.dropSeedVolume) &&
              `${drop.dropSeedVolume} μL s`,
            isLayersAndValueValid(expData, ELayersDetails.FRAGMENT, drop.dropFragment) &&
              `${drop.dropFragment} μL f`,
            drop.hasDropScreenLayer &&
              isLayersAndValueValid(
                expData,
                ELayersDetails.SCREEN,
                drop.dropScreenLayerVolume,
              ) &&
              `${drop.dropScreenLayerVolume} μL a`,
          ].filter(Boolean);
          const result = `Drop: ${parts.join(' + ')}`;

          const dropVolumesArray: any[] = [];
          dropVolumesArray.push({
            text: parts.length && result,
            colSpan: columnCount,
            border: [true, false, true, false],
          });

          for (let i = 1; i < columnCount; i++) {
            dropVolumesArray.push({});
          }

          body.push(dropVolumesArray);
        }

        if (options.showWellVolumes) {
          const wellVolumeArray: any[] = [];
          wellVolumeArray.push({
            text:
              (wellData[dropImageCount].wellIngredientList.length > 0
                ? 'Well: ' + wellData[dropImageCount].wellVolume + 'μL + '
                : ' ') +
              wellData[dropImageCount].wellWaterVolume +
              'μL H2O ',
            colSpan: columnCount,
            border: [true, false, true, false],
          });

          for (let i = 1; i < columnCount; i++) {
            wellVolumeArray.push({});
          }

          body.push(wellVolumeArray);
        }

        if (wellData[dropImageCount].wellIngredientList.length > 0) {
          if (options.showColumnNameHeading) {
            body.push(ingredientDataOptions);
          }

          let highlightRow = false;

          for (let i = 0; i < options.maxIngredients; i++) {
            const ingredient = wellData[dropImageCount].wellIngredientList[i];
            const ingLongName = ingredient?.ingredientStockIngredient.longName;
            const ingShortName = ingredient?.ingredientStockIngredient.shortName;
            const ingConcentration = ingredient?.concentration;
            const ingConcUnit = ingredient?.concentrationUnit;
            const ingpH = ingredient?.pH;
            const lowVolume = ingredient?.isBuffer
              ? ingredient?.lowPHVolume
              : ingredient?.volume;
            const highVolume = ingredient?.highPHVolume;

            let fillColor;
            if (highlightRow) {
              fillColor = '#D3D3D3';
            } else {
              fillColor = '#FFFFFF';
            }
            const ingNameArray: any = [];
            ingNameArray.push({
              text: ingredient
                ? options.ingredientNamePreference == 'longName'
                  ? ingLongName.length > maxChars
                    ? ingLongName.slice(0, maxChars) + '...'
                    : ingLongName
                  : ingShortName
                : ' ',
              colSpan: columnCount,
              alignment: 'center',
              fillColor: fillColor,
              border: [true, true, true, true],
            });

            for (let i = 1; i < columnCount; i++) {
              ingNameArray.push({});
            }

            body.push(ingNameArray);

            const ingDataArray: any = [];

            if (options.concentration) {
              ingDataArray.push({
                text: ingConcentration
                  ? Number(ingConcentration?.toFixed(1)) + ingConcUnit
                  : null,
                fillColor: fillColor,
                border: [true, true, true, true],
                alignment: 'center',
              });
            }
            if (options.pHVolumes) {
              ingDataArray.push({
                text: lowVolume ? Number(lowVolume?.toFixed(1)) : null,
                fillColor: fillColor,
                border: [true, true, true, true],
                alignment: 'center',
              });

              ingDataArray.push({
                text: highVolume ? Number(highVolume?.toFixed(1)) : null,
                fillColor: fillColor,
                border: [true, true, true, true],
                alignment: 'center',
              });
            }
            if (options.pH) {
              ingDataArray.push({
                text: ingpH ? Number(ingpH?.toFixed(1)) : null,
                fillColor: fillColor,
                border: [true, true, true, true],
                alignment: 'center',
              });
            }

            body.push(ingDataArray);
            highlightRow = !highlightRow;
          }
        } else {
          const fillColor = '#FFFFFF';

          const headerArray: any = [];
          headerArray.push({
            text: 'header',
            colSpan: columnCount,
            alignment: 'center',
            fillColor: fillColor,
            color: fillColor,
            border: [true, false, true, false],
          });

          for (let i = 1; i < columnCount; i++) {
            headerArray.push({});
          }
          body.push(headerArray);

          for (let i = 0; i < options.maxIngredients; i++) {
            for (let j = 0; j < 2; j++) {
              const ingArray: any[] = [];
              ingArray.push({
                text: 'header',
                colSpan: columnCount,
                alignment: 'center',
                fillColor: fillColor,
                color: fillColor,
                border: [true, false, true, false],
              });

              for (let i = 1; i < columnCount; i++) {
                ingArray.push({});
              }
              body.push(ingArray);
            }
          }
        }

        const columnWidths = [];

        let concentrationWidth = 0;
        let otherColumnWidth = 0;

        if (columnCount === 1) {
          concentrationWidth = 100;
          if (options.pH) {
            concentrationWidth = 0;
            otherColumnWidth = 100;
          }
        } else if (columnCount === 2) {
          concentrationWidth = 60;
          otherColumnWidth = 40;
          if (options.pHVolumes) {
            otherColumnWidth = 50;
            concentrationWidth = 0;
          }
        } else if (columnCount === 3) {
          if (options.concentration) {
            concentrationWidth = 50;
            otherColumnWidth = 25;
          } else {
            otherColumnWidth = 33.33;
          }
        } else if (columnCount === 4) {
          concentrationWidth = 40;
          otherColumnWidth = 20;
        }

        if (options.concentration) {
          columnWidths.push(concentrationWidth.toString() + '%');
        }
        if (options.pHVolumes) {
          columnWidths.push(otherColumnWidth.toString() + '%');
          columnWidths.push(otherColumnWidth.toString() + '%');
        }
        if (options.pH) {
          columnWidths.push(otherColumnWidth.toString() + '%');
        }

        columns.push([
          {
            stack: [
              {
                fontSize: fontSize,
                margin: [-5, -2, -5, -2],
                table: {
                  heights: tableHeight / options.rowLimit / rowCount,
                  widths: columnWidths,
                  body: body,
                },
                layout: {
                  hLineWidth: function hLineWidth(i) {
                    return 0.1;
                  },
                  vLineWidth: function vLineWidth(i) {
                    return 1;
                  },
                  paddingLeft: function (i, node) {
                    return 0;
                  },
                  paddingRight: function (i, node) {
                    return 0;
                  },
                  paddingTop: function (i, node) {
                    const text = node.table.body[i][0]._inlines;
                    if (text && text.length > 0) {
                      const textHeight = text[0].height;
                      const margin = (node.table.heights - textHeight) / 2;
                      node.table.body[i].forEach((cell) => {
                        if (cell._margin) {
                          cell._margin[1] = margin;
                        } else {
                          cell._margin = [0, margin, 0, 0];
                        }
                      });
                    }
                    return 0;
                  },
                  paddingBottom: function (i, node) {
                    return 0;
                  },
                },
              },
            ],
          },
        ]);
        dropImageCount++;
      }
    }
    rows.push({
      alignment: 'center',
      columns: columns,
    });
    columns = [];
  }
  return rows;
}

async function getWellData(
  expData: ExperimentDtoClient,
  wellInfo: any,
  dropNumber: number,
) {
  const plateData = expData.plateDto.find((plt) => plt.plateId == wellInfo.plateID);
  const wellList: any[] = [];

  plateData.ingredients.wellList.forEach((well) => {
    const wellData: any = {};
    wellData.wellName = well.rowLetter + well.columnNumber.toString();
    wellData.wellNumber = well.wellNumber;
    wellData.plateId = plateData.plateId;
    wellData.wellVolume = well.wellVolume;
    wellData.dropNumber = dropNumber > 0 ? dropNumber : null;
    wellData.wellWaterVolume = well.wellWaterVolume;

    let dropData: any = {};
    dropData = well.dropList.find((drp) => drp.dropNumber == dropNumber);

    wellData.dropProteinVolume = dropData?.dropProteinVolume;
    wellData.dropWellVolume = dropData?.dropWellVolume;
    wellData.dropSeedVolume = dropData?.dropSeedVolume;
    wellData.dropVolume = dropData?.volume;
    wellData.proteinFormulationName =
      plateData.ingredients.proteinFormulation[dropData?.proteinFormulationID]
        ?.proteinInfo.name || undefined;
    wellData.dropFragment = dropData?.dropFragmentVolume;
    wellData.wellIngredientList = [];

    well.wellLayerList.forEach((wellLyr) => {
      wellLyr.wellLayerIngredientList.forEach((ing) => {
        wellData.wellIngredientList.push(ing);
      });
    });

    const wellScreenLayer = expData.layers.find(
      (l) =>
        l.type === LayerType.Screen &&
        well.wellLayerList.map((wl) => wl.layerId).includes(l.id),
    );

    wellData.wellScreenLayerName = wellScreenLayer ? wellScreenLayer.name : '';

    const screenLayer = expData.layers.find((layer) => layer.type === LayerType.Screen);
    const dropScreenLayer = expData.layers.find(
      (layer) => layer.type === LayerType.Screen && layer.targetDrop === dropNumber,
    );
    const matchingDropScreenLayer = dropData?.dropLayerList?.find(
      (l) => l.layerId === dropScreenLayer?.id,
    );
    wellData.screenLayerVolume = screenLayer?.volume;
    wellData.hasDropScreenLayer = !!matchingDropScreenLayer;
    wellData.dropScreenLayerVolume = dropScreenLayer?.volume;

    wellList.push(wellData);
  });

  return wellList;
}

function getHeader() {
  return [
    {
      columns: [
        {
          image: Logo,
          width: 20,
          height: 20,
        },
        {
          text: 'ROCK MAKER®',
          margin: [5, 4, 0, 0],
          width: 440,
          fontSize: 12,
          color: '#707070',
        },
        {
          text: 'Printed On: ' + new Date().toLocaleDateString('en-US'),
          margin: [0, 5, 0, 0],
          color: '#707070',
          alignment: 'right',
        },
      ],
      margin: [20, 5, 20, 20],
    },
  ];
}

const getScoreTableLeftMagin = (options: IngredientReportOptionsDTO) => {
  let tableWidth = 0;
  let columnWidth = 0;
  if (options.rowLimit > options.columnLimit) {
    tableWidth = 420;
    tableWidth = tableWidth - (1 / 100) * tableWidth;
    columnWidth = tableWidth / options.columnLimit;
  } else {
    tableWidth = 500;
    tableWidth = tableWidth - (1 / 100) * tableWidth;
    columnWidth = tableWidth / options.columnLimit;
  }

  return -columnWidth;
};
